import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Layers, FolderTree, Trash2, Save, Eye, Link2, Search } from 'lucide-react'
import { adminApi } from '@/api/admin'
import type { AdminContent } from '@/api/admin'
import { handleApiError } from '@/api/client'
import {
  getQuizSets,
  getQuizSet,
  updateQuizSet,
  deleteQuizSet,
} from '@/api/quizSet'
import { getQuizGroups, getQuizGroup, updateQuizGroup, deleteQuizGroup } from '@/api/quizGroup'
import {
  getCurriculumModuleGroupSummary,
  getCurriculumQuizGroupAssociations,
  updateQuizGroupAssociationActive,
  updateQuizGroupBiggoMigrationStatus,
} from '@/api/quizGroupAssociation'
import type {
  BiggoMigrationStatus,
  QuizGroupAssociation,
  QuizSetMember,
  QuizGroupItem,
  ResolvedQuiz,
} from '@/types/quizSet'
import { ContentLevelBadge, ContentPickerModal } from '@/components/content/ContentPickerModal'
import { QuizSetInspector } from '@/components/quizset/QuizSetInspector'
import {
  AssociationSectionTabs,
  DEFAULT_ASSOCIATION_SECTION_TAB,
  type AssociationSectionTab,
} from '@/components/quizset/AssociationSectionTabs'
import { CurriculumModuleGroupSummaryPanel } from '@/components/quizset/CurriculumModuleGroupSummary'
import { LessonAssociationTable } from '@/components/quizset/LessonAssociationTable'
import {
  ALL_ASSOCIATION_SOURCES,
  filterAssociationsBySource,
  getAssociationSourceLabel,
  summarizeAssociationSources,
  type AssociationSourceSummary,
} from './quizSetAssociationSource'
import {
  buildLessonAssociationRows,
  getCurriculumLessonIds,
} from './quizSetAssociationHierarchy'
import { shouldShowAssociationInitialLoading } from './quizSetAssociationViewState'
import {
  ALL_BIGGO_MIGRATION_STATUSES,
  BIGGO_MIGRATION_STATUS_OPTIONS,
  filterAssociationsByBiggoMigrationStatus,
} from './quizGroupBiggoMigrationStatus'
import { flattenSetQuizIds, flattenGroupQuizIds } from '@/utils/quizSetQuizIds'

type Tab = 'set' | 'group' | 'association'

export default function QuizSetManagement() {
  const [tab, setTab] = useState<Tab>('set')
  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <h1 className="text-xl font-bold text-slate-800">문제 세트 관리</h1>
        <span className="text-sm text-slate-400">(조회 · 메타데이터 편집 — 문제 구성은 마이그레이션)</span>
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <TabButton active={tab === 'set'} onClick={() => setTab('set')} icon={<Layers className="w-4 h-4" />} label="세트" />
        <TabButton active={tab === 'group'} onClick={() => setTab('group')} icon={<FolderTree className="w-4 h-4" />} label="그룹" />
        <TabButton active={tab === 'association'} onClick={() => setTab('association')} icon={<Link2 className="w-4 h-4" />} label="레슨 연결" />
      </div>
      {tab === 'set' ? <SetPanel /> : tab === 'group' ? <GroupPanel /> : <AssociationPanel />}
    </div>
  )
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
        active ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

function QuizRow({ quiz, type }: { quiz: ResolvedQuiz; type?: string }) {
  return (
    <div className="flex items-center gap-2 py-1 text-sm">
      {type && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">{type}</span>}
      <span className="font-mono text-xs text-slate-400">#{quiz.quizId}</span>
      <span className="text-slate-800">{quiz.title ?? '(제목 없음)'}</span>
      {quiz.type && <span className="text-xs text-slate-400">· {quiz.type}</span>}
      {quiz.difficultyLevel != null && <span className="text-xs text-slate-400">· Lv{quiz.difficultyLevel}</span>}
    </div>
  )
}

// ===================== Set Panel =====================
interface QuizSetFormState {
  name: string
  description: string
  levelType: string
  levelValue: string
  grade: number | null
  semester: number | null
  unitNo: number | null
  unitName: string
  isActive: boolean
  sortOrder: number
}

function SetPanel() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [inspecting, setInspecting] = useState(false)

  const { data: sets = [] } = useQuery({ queryKey: ['quiz-sets'], queryFn: () => getQuizSets() })
  const { data: detail } = useQuery({
    queryKey: ['quiz-set', selectedId],
    queryFn: () => getQuizSet(selectedId!),
    enabled: selectedId != null,
  })

  const quizIds = useMemo(() => (detail ? flattenSetQuizIds(detail.members) : []), [detail])

  // 다른 세트를 선택하면 검수 모드를 빠져나간다
  useEffect(() => {
    setInspecting(false)
  }, [selectedId])

  const [form, setForm] = useState<QuizSetFormState>({
    name: '',
    description: '',
    levelType: '',
    levelValue: '',
    grade: null,
    semester: null,
    unitNo: null,
    unitName: '',
    isActive: true,
    sortOrder: 0,
  })
  useEffect(() => {
    if (detail) {
      setForm({
        name: detail.name,
        description: detail.description ?? '',
        levelType: detail.levelType ?? '',
        levelValue: detail.levelValue ?? '',
        grade: detail.grade ?? null,
        semester: detail.semester ?? null,
        unitNo: detail.unitNo ?? null,
        unitName: detail.unitName ?? '',
        isActive: detail.isActive,
        sortOrder: detail.sortOrder,
      })
    }
  }, [detail])

  const save = useMutation({
    mutationFn: () =>
      updateQuizSet(selectedId!, {
        name: form.name,
        description: form.description || null,
        levelType: form.levelType || null,
        levelValue: form.levelValue || null,
        grade: form.grade,
        semester: form.semester,
        unitNo: form.unitNo,
        unitName: form.unitName || null,
        isActive: form.isActive,
        sortOrder: form.sortOrder,
      }),
    onSuccess: () => {
      toast.success('저장되었습니다')
      qc.invalidateQueries({ queryKey: ['quiz-sets'] })
      qc.invalidateQueries({ queryKey: ['quiz-set', selectedId] })
    },
    onError: (e) => toast.error(handleApiError(e)),
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteQuizSet(id),
    onSuccess: () => {
      toast.success('삭제되었습니다')
      setSelectedId(null)
      qc.invalidateQueries({ queryKey: ['quiz-sets'] })
    },
    onError: (e) => toast.error(handleApiError(e)),
  })

  if (inspecting && detail) {
    return (
      <QuizSetInspector
        title={detail.name}
        quizIds={quizIds}
        onClose={() => setInspecting(false)}
      />
    )
  }

  return (
    <div className="grid grid-cols-[320px_1fr] gap-4">
      <div className="border border-slate-200 rounded-lg divide-y max-h-[70vh] overflow-auto">
        {sets.length === 0 && <div className="p-4 text-sm text-slate-400">세트가 없습니다 (마이그레이션 전)</div>}
        {sets.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelectedId(s.id)}
            className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${selectedId === s.id ? 'bg-blue-50' : ''}`}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800">{s.name}</span>
              {!s.isActive && <span className="text-xs text-red-400">비활성</span>}
            </div>
            <div className="text-xs text-slate-400">
              {s.levelType && `${s.levelType}:${s.levelValue ?? ''} · `}문제 {s.memberCount}
            </div>
          </button>
        ))}
      </div>

      <div>
        {!detail ? (
          <div className="text-sm text-slate-400 p-4">세트를 선택하세요</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="이름"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="정렬순서"><input type="number" className="input" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} /></Field>
              <Field label="level type"><input className="input" value={form.levelType} onChange={(e) => setForm({ ...form, levelType: e.target.value })} placeholder="grade / age ..." /></Field>
              <Field label="level value"><input className="input" value={form.levelValue} onChange={(e) => setForm({ ...form, levelValue: e.target.value })} placeholder="1 / 8 ..." /></Field>
              {form.levelType.trim().toLowerCase() === 'unit_assessment' && (
                <>
                  <Field label="학년"><input type="number" min={1} className="input" value={form.grade ?? ''} onChange={(e) => setForm({ ...form, grade: e.target.value ? Number(e.target.value) : null })} /></Field>
                  <Field label="학기"><input type="number" min={1} className="input" value={form.semester ?? ''} onChange={(e) => setForm({ ...form, semester: e.target.value ? Number(e.target.value) : null })} /></Field>
                  <Field label="단원 번호"><input type="number" min={1} className="input" value={form.unitNo ?? ''} onChange={(e) => setForm({ ...form, unitNo: e.target.value ? Number(e.target.value) : null })} /></Field>
                  <Field label="단원명"><input className="input" value={form.unitName} onChange={(e) => setForm({ ...form, unitName: e.target.value })} /></Field>
                </>
              )}
              {form.levelType.trim() === 'grade' && (
                <Field label="학기 (1·2, 초1·초2는 비움)"><input type="number" min={1} max={2} className="input" value={form.semester ?? ''} onChange={(e) => setForm({ ...form, semester: e.target.value ? Number(e.target.value) : null })} /></Field>
              )}
              <Field label="설명"><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <Field label="활성">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> 활성</label>
              </Field>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setInspecting(true)}
                disabled={quizIds.length === 0}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4" /> 검수 보기
              </button>
              <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary btn-md"><Save className="w-4 h-4" /> 메타 저장</button>
              <button onClick={() => { if (window.confirm('이 세트를 삭제할까요?')) remove.mutate(detail.id) }} className="btn-danger btn-md"><Trash2 className="w-4 h-4" /> 삭제</button>
            </div>

            <div className="border-t pt-3">
              <div className="text-sm font-semibold text-slate-600 mb-2">멤버 ({detail.members.length}) — 읽기 전용</div>
              <div className="space-y-2">
                {detail.members.map((m: QuizSetMember, i) => (
                  <div key={i} className="border border-slate-100 rounded p-2">
                    {m.kind === 'quiz' && m.quiz ? (
                      <QuizRow quiz={m.quiz} />
                    ) : m.group ? (
                      <div>
                        <div className="text-sm font-medium text-slate-700">📁 {m.group.name} <span className="text-xs text-slate-400">(그룹 · {m.group.items.length}문제)</span></div>
                        <div className="pl-4">
                          {m.group.items.map((it: QuizGroupItem, j) => <QuizRow key={j} quiz={it.quiz} type={it.type} />)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ===================== Group Panel =====================
function GroupPanel() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [inspecting, setInspecting] = useState(false)

  const { data: groups = [] } = useQuery({ queryKey: ['quiz-groups'], queryFn: () => getQuizGroups() })
  const { data: detail } = useQuery({
    queryKey: ['quiz-group', selectedId],
    queryFn: () => getQuizGroup(selectedId!),
    enabled: selectedId != null,
  })

  const quizIds = useMemo(() => (detail ? flattenGroupQuizIds(detail.items) : []), [detail])

  // 다른 그룹을 선택하면 검수 모드를 빠져나간다
  useEffect(() => {
    setInspecting(false)
  }, [selectedId])

  const [form, setForm] = useState({ name: '', description: '', isActive: true, sortOrder: 0 })
  useEffect(() => {
    if (detail) setForm({ name: detail.name, description: detail.description ?? '', isActive: detail.isActive, sortOrder: detail.sortOrder })
  }, [detail])

  const save = useMutation({
    mutationFn: () => updateQuizGroup(selectedId!, { name: form.name, description: form.description || null, isActive: form.isActive, sortOrder: form.sortOrder }),
    onSuccess: () => {
      toast.success('저장되었습니다')
      qc.invalidateQueries({ queryKey: ['quiz-groups'] })
      qc.invalidateQueries({ queryKey: ['quiz-group', selectedId] })
    },
    onError: (e) => toast.error(handleApiError(e)),
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteQuizGroup(id),
    onSuccess: () => {
      toast.success('삭제되었습니다')
      setSelectedId(null)
      qc.invalidateQueries({ queryKey: ['quiz-groups'] })
    },
    onError: (e) => toast.error(handleApiError(e)), // GROUP_IN_USE 메시지 노출
  })

  if (inspecting && detail) {
    return (
      <QuizSetInspector
        title={detail.name}
        quizIds={quizIds}
        onClose={() => setInspecting(false)}
      />
    )
  }

  return (
    <div className="grid grid-cols-[320px_1fr] gap-4">
      <div className="border border-slate-200 rounded-lg divide-y max-h-[70vh] overflow-auto">
        {groups.length === 0 && <div className="p-4 text-sm text-slate-400">그룹이 없습니다 (마이그레이션 전)</div>}
        {groups.map((g) => (
          <button key={g.id} onClick={() => setSelectedId(g.id)} className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${selectedId === g.id ? 'bg-blue-50' : ''}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800">{g.name}</span>
              {g.sourceSystem && <span className="text-xs text-blue-500">{g.sourceSystem}</span>}
              {!g.isActive && <span className="text-xs text-red-400">비활성</span>}
            </div>
            <div className="text-xs text-slate-400">문제 {g.memberCount}</div>
          </button>
        ))}
      </div>

      <div>
        {!detail ? (
          <div className="text-sm text-slate-400 p-4">그룹을 선택하세요</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="이름"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="정렬순서"><input type="number" className="input" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} /></Field>
              <Field label="설명"><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <Field label="활성"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> 활성</label></Field>
              <ReadOnlyField label="source system" value={detail.sourceSystem} />
              <ReadOnlyField label="source group key" value={detail.sourceGroupKey} mono />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setInspecting(true)}
                disabled={quizIds.length === 0}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4" /> 검수 보기
              </button>
              <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary btn-md"><Save className="w-4 h-4" /> 메타 저장</button>
              <button onClick={() => { if (window.confirm('이 그룹을 삭제할까요?')) remove.mutate(detail.id) }} className="btn-danger btn-md"><Trash2 className="w-4 h-4" /> 삭제</button>
            </div>
            <div className="border-t pt-3">
              <div className="text-sm font-semibold text-slate-600 mb-2">문제 ({detail.items.length}) — 읽기 전용</div>
              <div className="space-y-1">
                {detail.items.map((it: QuizGroupItem, i) => <QuizRow key={i} quiz={it.quiz} type={it.type} />)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ===================== Lesson Association Panel =====================
function AssociationPanel() {
  const queryClient = useQueryClient()
  const [selectedCurriculum, setSelectedCurriculum] = useState<AdminContent | null>(null)
  const [isCurriculumPickerOpen, setIsCurriculumPickerOpen] = useState(false)
  const [associationTab, setAssociationTab] = useState<AssociationSectionTab>(DEFAULT_ASSOCIATION_SECTION_TAB)
  const [activeOnly, setActiveOnly] = useState(false)
  const [sourceFilter, setSourceFilter] = useState(ALL_ASSOCIATION_SOURCES)
  const [biggoStatusFilter, setBiggoStatusFilter] = useState(ALL_BIGGO_MIGRATION_STATUSES)
  const curriculumId = selectedCurriculum?.id ?? null

  const associationsQuery = useQuery({
    queryKey: ['quiz-group-associations', 'curriculum-detail', curriculumId, activeOnly],
    queryFn: () => getCurriculumQuizGroupAssociations(curriculumId!, 'global_aiq', activeOnly),
    enabled: curriculumId != null,
  })
  const associations = associationsQuery.data ?? []

  const hierarchyQuery = useQuery({
    queryKey: ['content-hierarchy', curriculumId, false],
    queryFn: () => adminApi.getContentHierarchy(curriculumId!, false),
    enabled: curriculumId != null,
  })

  const curriculumSummaryQuery = useQuery({
    queryKey: [
      'quiz-group-associations',
      'curriculum-module-summary',
      curriculumId,
    ],
    queryFn: () => getCurriculumModuleGroupSummary(curriculumId!),
    enabled: curriculumId != null,
  })

  const updateAssociationActive = useMutation({
    mutationFn: ({ associationId, isActive }: {
      associationId: number
      isActive: boolean
      curriculumId: number
      activeOnly: boolean
    }) =>
      updateQuizGroupAssociationActive(associationId, isActive),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<QuizGroupAssociation[]>(
        ['quiz-group-associations', 'curriculum-detail', variables.curriculumId, variables.activeOnly],
        (current) => {
          if (!current) return current
          if (variables.activeOnly && !updated.isActive) {
            return current.filter((association) => association.id !== updated.id)
          }
          return current.map((association) => association.id === updated.id ? updated : association)
        },
      )
      void queryClient.invalidateQueries({
        queryKey: ['quiz-group-associations', 'curriculum-detail', variables.curriculumId],
      })
      void queryClient.invalidateQueries({
        queryKey: ['quiz-group-associations', 'curriculum-module-summary', variables.curriculumId],
      })
      toast.success(updated.isActive ? '레슨 연결을 활성화했습니다.' : '레슨 연결을 비활성화했습니다.')
    },
    onError: (mutationError) => toast.error(handleApiError(mutationError)),
  })

  const updateBiggoMigrationStatus = useMutation({
    mutationFn: ({ associationId, status }: {
      associationId: number
      status: BiggoMigrationStatus
      curriculumId: number
    }) => updateQuizGroupBiggoMigrationStatus(associationId, status),
    onSuccess: (updated, variables) => {
      queryClient.setQueriesData<QuizGroupAssociation[]>(
        { queryKey: ['quiz-group-associations', 'curriculum-detail', variables.curriculumId] },
        (current) => current?.map((association) => (
          association.group.id === updated.group.id
            ? {
                ...association,
                group: {
                  ...association.group,
                  biggoMigrationStatus: updated.group.biggoMigrationStatus,
                },
              }
            : association
        )),
      )
      toast.success('Biggo 검수 상태를 저장했습니다.')
    },
    onError: (mutationError) => toast.error(handleApiError(mutationError)),
  })

  const curriculumLessonIds = useMemo(
    () => getCurriculumLessonIds(hierarchyQuery.data),
    [hierarchyQuery.data],
  )
  const curriculumAssociations = useMemo(
    () => associations.filter((association) => curriculumLessonIds.has(association.contentId)),
    [associations, curriculumLessonIds],
  )
  const sourceSummaries = useMemo(
    () => summarizeAssociationSources(curriculumAssociations),
    [curriculumAssociations],
  )
  const filteredAssociations = useMemo(
    () => filterAssociationsByBiggoMigrationStatus(
      filterAssociationsBySource(curriculumAssociations, sourceFilter),
      biggoStatusFilter,
    ),
    [biggoStatusFilter, curriculumAssociations, sourceFilter],
  )
  const associationRows = useMemo(
    () => buildLessonAssociationRows(hierarchyQuery.data, filteredAssociations),
    [hierarchyQuery.data, filteredAssociations],
  )
  const totalItems = useMemo(
    () => filteredAssociations.reduce(
      (sum, association) => sum + association.group.items.length,
      0,
    ),
    [filteredAssociations],
  )
  const allSourceItems = useMemo(
    () => sourceSummaries.reduce((sum, summary) => sum + summary.itemCount, 0),
    [sourceSummaries],
  )
  const selectedSourceLabel = sourceFilter === ALL_ASSOCIATION_SOURCES
    ? '전체 출처'
    : getAssociationSourceLabel(sourceFilter)
  const detailLoading = shouldShowAssociationInitialLoading(associationsQuery, hierarchyQuery)
  const detailError = associationsQuery.data == null
    ? associationsQuery.error
    : hierarchyQuery.data == null
      ? hierarchyQuery.error
      : null

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-600">검수 범위</p>
          <h2 className="mt-1 text-lg font-bold text-slate-900">커리큘럼 선택</h2>
          <p className="mt-1 text-sm text-slate-500">선택한 커리큘럼의 모듈 요약과 레슨 연결을 하나의 범위로 검수합니다.</p>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setIsCurriculumPickerOpen(true)}
            className={`flex min-h-12 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
              selectedCurriculum
                ? 'border-blue-300 bg-blue-50/50 ring-2 ring-blue-100'
                : 'border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/30'
            }`}
          >
            {selectedCurriculum ? (
              <>
                <ContentLevelBadge level={selectedCurriculum.hierarchyLevel} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-800">{selectedCurriculum.title}</span>
                  <span className="block font-mono text-xs text-slate-400">Content #{selectedCurriculum.id}</span>
                </span>
                <span className="text-xs font-semibold text-blue-600">변경</span>
              </>
            ) : (
              <>
                <span className="rounded-md bg-slate-100 p-2 text-slate-500"><Search className="size-4" /></span>
                <span className="text-sm font-medium text-slate-500">커리큘럼을 검색해 선택하세요</span>
              </>
            )}
          </button>
        </div>
      </section>

      <AssociationSectionTabs
        activeTab={associationTab}
        associationCount={selectedCurriculum && !detailLoading ? curriculumAssociations.length : null}
        onChange={setAssociationTab}
      />

      {associationTab === 'detail' ? (
        <section
          id="lesson-association-detail-panel"
          role="tabpanel"
          aria-labelledby="lesson-association-detail-tab"
          className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-semibold text-slate-900">레슨 연결 상세</h2>
              <p className="mt-1 text-xs text-slate-500">커리큘럼 → 코스 → 모듈 → 레슨 순으로 Q / S / QQS 구성을 확인합니다.</p>
            </div>
            {selectedCurriculum ? (
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={activeOnly}
                    onChange={(event) => setActiveOnly(event.target.checked)}
                  />
                  활성 연결만 보기
                </label>
                <Field label="source system 필터">
                  <AssociationSourceSelect
                    value={sourceFilter}
                    summaries={sourceSummaries}
                    onChange={setSourceFilter}
                  />
                </Field>
                <Field label="Biggo 검수 필터">
                  <select
                    className="input w-40"
                    value={biggoStatusFilter}
                    onChange={(event) => setBiggoStatusFilter(event.target.value)}
                  >
                    <option value={ALL_BIGGO_MIGRATION_STATUSES}>전체 상태</option>
                    {BIGGO_MIGRATION_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
            ) : null}
          </div>

          {!selectedCurriculum ? (
            <CurriculumSelectionEmptyState onSelect={() => setIsCurriculumPickerOpen(true)} />
          ) : detailError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">{handleApiError(detailError)}</div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Metric label="커리큘럼" value={`#${selectedCurriculum.id}`} />
                <Metric label="출처" value={selectedSourceLabel} />
                <Metric label="연결 그룹" value={String(filteredAssociations.length)} />
                <Metric label="문제" value={String(totalItems)} />
              </div>

              {!detailLoading && curriculumAssociations.length > 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-2 text-xs font-semibold text-slate-500">출처별 현황</div>
                  <div className="flex flex-wrap gap-2">
                    <SourceSummaryButton
                      active={sourceFilter === ALL_ASSOCIATION_SOURCES}
                      label="전체 출처"
                      associationCount={curriculumAssociations.length}
                      itemCount={allSourceItems}
                      onClick={() => setSourceFilter(ALL_ASSOCIATION_SOURCES)}
                    />
                    {sourceSummaries.map((summary) => (
                      <SourceSummaryButton
                        key={summary.key}
                        active={sourceFilter === summary.key}
                        label={summary.label}
                        associationCount={summary.associationCount}
                        itemCount={summary.itemCount}
                        onClick={() => setSourceFilter(summary.key)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {detailLoading ? (
                <div role="status" className="rounded-lg border border-slate-200 p-8 text-center text-sm text-slate-400">
                  커리큘럼 계층과 레슨 연결을 불러오는 중입니다...
                </div>
              ) : (
                <LessonAssociationTable
                  rows={associationRows}
                  updatingAssociationId={updateAssociationActive.isPending
                    ? updateAssociationActive.variables?.associationId ?? null
                    : null}
                  updatingBiggoStatusAssociationId={updateBiggoMigrationStatus.isPending
                    ? updateBiggoMigrationStatus.variables?.associationId ?? null
                    : null}
                  onActiveChange={(associationId, isActive) => {
                    updateAssociationActive.mutate({
                      associationId,
                      isActive,
                      curriculumId: curriculumId!,
                      activeOnly,
                    })
                  }}
                  onBiggoStatusChange={(associationId, status) => {
                    updateBiggoMigrationStatus.mutate({
                      associationId,
                      status,
                      curriculumId: curriculumId!,
                    })
                  }}
                />
              )}
            </div>
          )}
        </section>
      ) : (
        <section
          id="lesson-association-summary-panel"
          role="tabpanel"
          aria-labelledby="lesson-association-summary-tab"
          className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/50 p-4"
        >
          <div>
            <h2 className="font-semibold text-slate-900">커리큘럼 모듈 현황</h2>
            <p className="mt-1 text-xs text-slate-500">활성 global_aiq 연결을 모듈별로 집계하고 AIQ (qb_aiq)와 따대깨 (legacy_ttaedaeggae) 수를 나란히 비교합니다.</p>
          </div>
          {!selectedCurriculum ? (
            <CurriculumSelectionEmptyState onSelect={() => setIsCurriculumPickerOpen(true)} />
          ) : (
            <CurriculumModuleGroupSummaryPanel
              data={curriculumSummaryQuery.data}
              isLoading={curriculumSummaryQuery.isLoading}
              error={curriculumSummaryQuery.data == null ? curriculumSummaryQuery.error : null}
              onRetry={() => void curriculumSummaryQuery.refetch()}
            />
          )}
        </section>
      )}

      {isCurriculumPickerOpen ? (
        <ContentPickerModal
          allowedLevels={['curriculum']}
          title="커리큘럼 선택"
          description="모듈 현황과 레슨 연결을 검수할 커리큘럼을 선택하세요."
          onClose={() => setIsCurriculumPickerOpen(false)}
          onSelect={(content) => {
            setSelectedCurriculum(content)
            setSourceFilter(ALL_ASSOCIATION_SOURCES)
            setBiggoStatusFilter(ALL_BIGGO_MIGRATION_STATUSES)
            setIsCurriculumPickerOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}

function CurriculumSelectionEmptyState({ onSelect }: { onSelect: () => void }) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
      <span className="flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-500">
        <Search className="size-4" aria-hidden="true" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-700">검수할 커리큘럼을 선택하세요</p>
      <p className="mt-1 text-xs text-slate-400">선택 후 이 탭의 데이터를 바로 확인할 수 있습니다.</p>
      <button
        type="button"
        onClick={onSelect}
        className="mt-4 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-700"
      >
        커리큘럼 선택
      </button>
    </div>
  )
}

function SourceSummaryButton({
  active,
  label,
  associationCount,
  itemCount,
  onClick,
}: {
  active: boolean
  label: string
  associationCount: number
  itemCount: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-md border px-3 py-2 text-left transition-colors ${
        active
          ? 'border-blue-300 bg-blue-50 text-blue-700'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <div className="text-xs font-semibold">{label}</div>
      <div className="mt-0.5 text-xs opacity-75">연결 {associationCount} · 문제 {itemCount}</div>
    </button>
  )
}

function AssociationSourceSelect({
  value,
  summaries,
  onChange,
}: {
  value: string
  summaries: AssociationSourceSummary[]
  onChange: (value: string) => void
}) {
  const selectedSourceExists = value === ALL_ASSOCIATION_SOURCES
    || summaries.some((summary) => summary.key === value)
  return (
    <select
      className="input w-56"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value={ALL_ASSOCIATION_SOURCES}>전체 출처</option>
      {!selectedSourceExists && (
        <option value={value}>{getAssociationSourceLabel(value)} (0)</option>
      )}
      {summaries.map((summary) => (
        <option key={summary.key} value={summary.key}>{summary.label} · {summary.key}</option>
      ))}
    </select>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  )
}

function ReadOnlyField({ label, value, mono = false }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <Field label={label}>
      <div className={`min-h-10 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '-'}
      </div>
    </Field>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-800">{value}</div>
    </div>
  )
}
